import { Accordion, Button, MenuTrigger, Stack, Text } from '@eleveo/components-react';
import { FilterClearIcon, ThreeDotsIcon } from '@eleveo/icons-react';
// eslint-disable-next-line import/no-extraneous-dependencies
import { today } from '@internationalized/date';
import { equals } from 'ramda';
import React, { useState } from 'react';
// eslint-disable-next-line import/no-extraneous-dependencies
import { useDateFormatter } from 'react-aria';
import { Trans, useTranslation } from 'react-i18next';

import { useAuth } from '../../app/AuthorizationProvider';
import { Sidebar, SidebarContent, SidebarPanel, SidebarPart } from '../../components/LayoutNext/LayoutNext';
import {
  CheckboxFilterTile,
  getCheckboxFilterTileSubtitle,
  kpiItems,
  type CheckboxFilterTileProps,
} from '../../components/LayoutNext/components/CheckboxFilterTile';
import {
  DateRangeFilterTile,
  type DateRangeFilterTileProps,
} from '../../components/LayoutNext/components/DateRangeFilterTile';
import { isDateRangeFilterAccordionType } from '../../components/LayoutNext/components/filterTileUtils';
import { SupervisedAgentsAllResult, SupervisedTeamsResult } from '../../graphql/types';
import { ReportsKpiType, Values } from '../../types';
import { DateRangeFilter } from '../../utils/filter';
import { fullNameOfAgent } from '../../utils/helpers';
import {
  ACTIVE_SIDEBAR_FILTERS_COUNTER,
  APPLY_FILTER_BUTTON_TAG,
  FILTER_SIDEBAR_DROPDOWN_TOGGLE,
  RESET_ALL_SIDEBAR_FILTERS_BUTTON_TAG,
} from './constants';

export const getDefaultReportsAdherenceSidebarFilter = () =>
  ({
    kpi: [],
    dateRange: null as unknown as DateRangeFilter,
    teams: [],
    agents: [],
  } as ReportsAdherenceFilterSidebarState);

export type ReportsAdherenceFilterSidebarState = {
  kpi: Values<typeof ReportsKpiType>[];
  dateRange: DateRangeFilter;
  teams: string[];
  agents: string[];
};

export type ReportsAdherenceFilterSidebarProps = {
  agentsResult: SupervisedAgentsAllResult;
  handleCancel: () => void;
  preOpenedItemIds: string[];
  setPreOpenedItemIds: React.Dispatch<React.SetStateAction<string[]>>;
  setSidebarFilter: (filter: ReportsAdherenceFilterSidebarState) => void;
  sidebarFilter: ReportsAdherenceFilterSidebarState;
  teamsResult: SupervisedTeamsResult;
};

export const ReportsAdherenceFilterSidebar: React.FC<ReportsAdherenceFilterSidebarProps> = ({
  agentsResult,
  handleCancel,
  preOpenedItemIds,
  setPreOpenedItemIds,
  setSidebarFilter,
  sidebarFilter,
  teamsResult,
}) => {
  const { t } = useTranslation();

  const { timezone } = useAuth();

  const formatter = useDateFormatter({ dateStyle: 'short', timeZone: timezone });

  const kpi = kpiItems(t);
  const teams = teamsResult.data?.supervisedTeams ?? [];
  const agents =
    agentsResult.data?.supervisedAgents?.agents.map((agent) => ({ ...agent, name: fullNameOfAgent(agent) })) ?? [];

  const defaultSelectedKpiIds = sidebarFilter.kpi;
  const defaultSelectedTeamsIds = sidebarFilter.teams;
  const defaultSelectedAgentIds = sidebarFilter.agents;

  // Filters states
  const [selectedKpiIds, setSelectedKpiIds] = useState(defaultSelectedKpiIds);
  const [selectedDateRange, setSelectedDateRange] = useState<DateRangeFilter>(sidebarFilter.dateRange);
  const [selectedTeamIds, setSelectedTeamIds] = useState(defaultSelectedTeamsIds);
  const [selectedAgentIds, setSelectedAgentIds] = useState(defaultSelectedAgentIds);

  const handleApply = () => {
    setSidebarFilter({
      kpi: selectedKpiIds,
      dateRange: selectedDateRange,
      agents: selectedAgentIds,
      teams: selectedTeamIds,
    });
  };

  const handleResetAllFilters = () => {
    setSelectedKpiIds([]);
    setSelectedTeamIds([]);
    setSelectedAgentIds([]);
    setSelectedDateRange(null as unknown as DateRangeFilter);

    setPreOpenedItemIds([]);
  };

  const isDateRangeFilterActive = sidebarFilter.dateRange?.start && sidebarFilter.dateRange?.end;

  const countOfActiveFilters = [
    !!defaultSelectedKpiIds.length,
    !!isDateRangeFilterActive,
    !!defaultSelectedTeamsIds.length,
    !!defaultSelectedAgentIds.length,
  ].filter((active) => active).length;

  const isFilterActive =
    selectedKpiIds.length > 0 || selectedAgentIds.length > 0 || selectedTeamIds.length > 0 || isDateRangeFilterActive;

  const filterValuesPristine =
    equals(selectedKpiIds, defaultSelectedKpiIds) &&
    equals(selectedAgentIds, defaultSelectedAgentIds) &&
    equals(selectedTeamIds, defaultSelectedTeamsIds) &&
    equals(sidebarFilter.dateRange?.start, selectedDateRange?.start) &&
    equals(sidebarFilter.dateRange?.end, selectedDateRange?.end);

  // Accordion filter options
  const filters: [CheckboxFilterTileProps, DateRangeFilterTileProps, ...CheckboxFilterTileProps[]] = [
    {
      testId: 'reports-adherence-sidebar-filter-kpi',
      id: '1',
      isRequired: true,
      items: kpi,
      onSelectionChange: (keys) => setSelectedKpiIds(keys as Values<typeof ReportsKpiType>[]),
      selectedKeys: selectedKpiIds,
      subtitle: getCheckboxFilterTileSubtitle(t, kpi, selectedKpiIds),
      subtitleColor: 'info',
      title: t('reports.kpi'),
    },
    {
      testId: 'reports-adherence-sidebar-filter-range',
      id: '2',
      maxValue: today(timezone).subtract({ days: 1 }), // yesterday as maximum
      subtitle:
        selectedDateRange?.start && selectedDateRange?.end
          ? `${formatter.format(selectedDateRange?.start.toDate(timezone))} - ${formatter.format(
              selectedDateRange?.end.toDate(timezone)
            )}`
          : '',
      subtitleColor: 'info',
      title: t('date.dateRange'),
      values: selectedDateRange,
      setValues: setSelectedDateRange,
    },
    {
      testId: 'reports-adherence-sidebar-filter-teams',
      id: '3',
      items: teams,
      onSelectionChange: setSelectedTeamIds,
      selectedKeys: selectedTeamIds,
      sortByField: 'textValue',
      subtitle: getCheckboxFilterTileSubtitle(t, teams, selectedTeamIds),
      subtitleColor: 'info',
      title: t('team.team', { count: 2 }),
    },
    {
      testId: 'reports-adherence-sidebar-filter-agents',
      id: '4',
      items: agents,
      onSelectionChange: setSelectedAgentIds,
      selectedKeys: selectedAgentIds,
      subtitle: getCheckboxFilterTileSubtitle(t, agents, selectedAgentIds),
      subtitleColor: 'info',
      title: t('person.people'),
    },
  ];

  const isRequiredFieldsValid = !filters
    .filter((filter) => filter.isRequired)
    .find((filter) => !(filter.selectedKeys as string[])?.length && !filter.values);

  return (
    <Sidebar onClose={handleCancel} title={t('filter.filters')}>
      <SidebarPanel>
        <Stack direction="horizontal" className="w-full items-center justify-between">
          <Button
            testId={APPLY_FILTER_BUTTON_TAG}
            isDisabled={filterValuesPristine || !isRequiredFieldsValid}
            color="success"
            onClick={handleApply}
          >
            {t('form.apply')}
          </Button>
          <Text
            color={!countOfActiveFilters ? { light: 'grey-500', dark: 'grey-300' } : {}}
            testId={ACTIVE_SIDEBAR_FILTERS_COUNTER}
          >
            <Trans i18nKey="filter.activeFilters" values={{ count: countOfActiveFilters }}>
              {/* space needs to be added within strong tag so the tests can run correctly */}
              {/* @ts-expect-error: Value needs to be interpolated, using double bracelets */}
              <strong>{{ countOfActiveFilters }} </strong>Active filters
            </Trans>
          </Text>

          <MenuTrigger
            items={[
              {
                id: '1',
                text: t('filter.resetAllFilters'),
                onClick: handleResetAllFilters,
                icon: <FilterClearIcon />,
                isDisabled: !isFilterActive,
                testId: RESET_ALL_SIDEBAR_FILTERS_BUTTON_TAG,
              },
            ]}
          >
            <Button aria-label={t('filter.options')} variant="outline" testId={FILTER_SIDEBAR_DROPDOWN_TOGGLE}>
              <ThreeDotsIcon />
            </Button>
          </MenuTrigger>
        </Stack>
      </SidebarPanel>

      <SidebarContent>
        <SidebarPart>
          <Accordion
            expandedKeys={preOpenedItemIds}
            expansionMode="single"
            items={filters}
            onExpandedChange={(keys) => setPreOpenedItemIds([...keys] as string[])}
          >
            {(item) => {
              // Calendar Range filter type/content
              if (isDateRangeFilterAccordionType(item)) {
                return <DateRangeFilterTile {...item} />;
              }
              return <CheckboxFilterTile {...item} />;
            }}
          </Accordion>
        </SidebarPart>
      </SidebarContent>
    </Sidebar>
  );
};
